import type { DraftStore } from './autosave.ts';

export type StoredDraft<D> = { base: string; doc: D; at: number };

/**
 * Recoverable local draft for one item and one identity. Writes are throttled
 * (the server autosave is the source of truth); a pending write is flushed
 * when the page unloads.
 */
export function localDraftStore<D>(key: string): DraftStore<D> & { read(): StoredDraft<D> | null; flush(): void } {
  let pending: StoredDraft<D> | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const persist = () => {
    timer = undefined;
    if (!pending) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(pending));
    } catch {
      // Storage full or unavailable: the server copy and in-memory editor still hold the text.
    }
    pending = null;
  };
  return {
    write(base, doc) {
      pending = { base, doc, at: Date.now() };
      if (!timer) timer = setTimeout(persist, 300);
    },
    clear() {
      clearTimeout(timer);
      timer = undefined;
      pending = null;
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
    flush() {
      clearTimeout(timer);
      persist();
    },
    read() {
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as StoredDraft<D>) : null;
      } catch {
        return null;
      }
    },
  };
}

export const draftKey = (userKey: string, itemId: string) => `mystiq:draft:${userKey}:${itemId}`;
